import type { ReactNode } from 'react';
import { Field as CrowField, useFieldControl } from '@crow/ui/field';

export type FieldControlProps = {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': true | undefined;
};

/**
 * The design system's Field (`design-system/ui`, copied in `src/crow`): the label above its
 * control, the description under it, and the error in its place (announced as an alert). The
 * render function gives the control its `id`, `aria-describedby` and `aria-invalid`, for native
 * and Radix controls alike.
 */
export function Field({
  id,
  label,
  description,
  error,
  children,
  className,
}: {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  className?: string;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const hasError = error !== undefined && error !== null && error !== '';
  const hasDescription = description !== undefined && description !== null && description !== '';
  return (
    <CrowField
      id={id}
      label={label}
      hint={hasDescription ? description : undefined}
      error={hasError ? <span role="alert">{error}</span> : undefined}
      className={className}
    >
      <FieldControl render={children} />
    </CrowField>
  );
}

function FieldControl({ render }: { render: (props: FieldControlProps) => ReactNode }) {
  const field = useFieldControl();
  if (field === null) throw new Error('FieldControl renders inside a Field');
  return render({
    id: field.id,
    'aria-describedby': field.describedBy,
    'aria-invalid': field.invalid || undefined,
  });
}
